import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'
import { Button } from './Button'

export interface PaginationProps {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  className?: string
}

/**
 * Always states the range and the total ("21–40 of 184"). A bare page number
 * cannot answer "am I near the end", which is the question people actually ask.
 */
export function Pagination({ page, pageSize, total, onPageChange, className }: PaginationProps) {
  const t = useT()
  const lastPage = Math.max(1, Math.ceil(total / pageSize))
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3 px-1 py-3', className)}>
      <p className="text-sm text-fg-muted" aria-live="polite">
        {total === 0 ? t('pagination.noResults') : t('pagination.range', { from, to, total })}
      </p>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          leftIcon={<ChevronLeft className="h-4 w-4" />}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          {t('pagination.previous')}
        </Button>
        <span className="text-sm text-fg-subtle">
          {page} / {lastPage}
        </span>
        <Button
          size="sm"
          rightIcon={<ChevronRight className="h-4 w-4" />}
          disabled={page >= lastPage}
          onClick={() => onPageChange(page + 1)}
        >
          {t('pagination.next')}
        </Button>
      </div>
    </div>
  )
}
